import { secureEmailService } from './secureEmailService';
import { sendingDomainService } from './sendingDomainService';
import DevEmailService from './devEmailService';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import SystemEmailService from './systemEmailService';
import { ACTIVE_DOMAIN } from '../config/domainConfig';
import { generateUnsubscribeHeaders, shouldAddUnsubscribeHeaders } from '../utils/emailHeaders';
import { platformEmailService } from './platformEmailService';
import { Automation, AutomationAction } from './automationService';
import { mainAppUrl } from '../utils/appOrigin';

export interface EmailOptions {
  to: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  fromName?: string;
  templateId?: string;
  fromEmail?: string; // Custom from email for subdomain-based sending
  userId?: string; // User ID for getting sending domain
  workspaceId?: string; // Workspace ID for checking verified domains
  replyToEmail?: string; // Reply-to email address
  tags?: { [key: string]: string }; // Tags for email categorization (e.g., verification, system emails)
  unsubscribeUrl?: string; // Custom unsubscribe URL
  headers?: { [key: string]: string }; // Custom headers
}

export interface EmailResult {
  success: boolean;
  error?: string;
  messageId?: string;
  provider?: string | null;
  wasFallback?: boolean;
}

/** Passcode / signup verification must always use this envelope (no .in / subdomain / env overrides). */
export const PASSCODE_VERIFICATION_FROM_EMAIL = 'verify@scubamail.io';

/** Plan activation and billing notices (not passcode / auth). */
export const BILLING_FROM_EMAIL = 'billing@scubamail.io';

class EmailService {
  private static readonly PROVIDERS = {
    SES: 'ses'
  };

  // Main email sending method - now uses secure Amazon SES via Firebase Functions
  static async sendEmail(options: EmailOptions): Promise<EmailResult> {
    console.log('📧 Sending email via secure Amazon SES service...');
    const useDevEmail = process.env.REACT_APP_USE_DEV_EMAIL === 'true';

    if (useDevEmail) {
      console.log('📧 [DEV] Using development email service (no external request)');
      return await DevEmailService.sendEmail({
        to: options.to,
        subject: options.subject,
        htmlBody: options.htmlBody,
        textBody: options.textBody,
        fromName: options.fromName || 'Scubamail'
      });
    }

    try {
      // Use secure service which routes through Firebase Functions to Amazon SES
      return await this.sendViaSecureSES(options);
    } catch (error) {
      console.error('Email sending failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email'
      };
    }
  }

  // Amazon SES implementation via secure Firebase Functions
  private static async sendViaSecureSES(options: EmailOptions): Promise<EmailResult> {
    try {
      console.log('📧 Sending email via Amazon SES...');
      console.log('To:', options.to);
      console.log('Subject:', options.subject);

      // Determine from email and reply-to
      let fromEmail = options.fromEmail || process.env.REACT_APP_FROM_EMAIL || 'info@scubamail.io';
      let replyToEmail = options.replyToEmail;

      // If user ID is provided and fromEmail looks like a user email, try to get sending address
      // (sendingDomainService now checks for verified domains and returns direct email if verified)
      if (options.userId && options.fromEmail && !options.fromEmail.endsWith('@scubamail.io') && !options.fromEmail.endsWith('@scaledots.com')) {
        try {
          console.log('📧 Attempting to get sending address for:', options.fromEmail);
          const sendingAddress = await sendingDomainService.getSendingAddressForEmail(
            options.userId,
            options.fromEmail,
            options.workspaceId // Pass workspaceId for domain verification check
          );
          fromEmail = sendingAddress;

          // Check if this is a verified domain (sendingAddress equals original email)
          const isVerifiedDomainEmail = sendingAddress.toLowerCase() === options.fromEmail.toLowerCase();

          if (isVerifiedDomainEmail) {
            console.log('📧 ✅ Using verified domain email directly:', fromEmail);
            // For verified domains, Reply-To is optional (same as From)
            replyToEmail = options.replyToEmail || undefined; // Only set if explicitly provided
          } else {
            console.log('📧 Using subdomain-based sending address:', fromEmail);
            // Get reply-to email if not provided (for subdomain addresses)
            if (!replyToEmail) {
              const replyTo = await sendingDomainService.getReplyToEmail(sendingAddress);
              replyToEmail = replyTo || options.fromEmail; // Fallback to original email
            }
          }
        } catch (error) {
          console.warn('⚠️ Failed to get sending address, generating manually:', error);
          // Manual fallback: generate subdomain format directly
          const [localPart, domain] = options.fromEmail.split('@');
          const cleanLocalPart = localPart.replace(/[^a-zA-Z0-9]/g, '-');
          const freeEmailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
          if (freeEmailDomains.includes(domain.toLowerCase())) {
            fromEmail = `${cleanLocalPart}.${domain}@${ACTIVE_DOMAIN.sendingDomain}`;
          } else {
            fromEmail = `${cleanLocalPart}@${ACTIVE_DOMAIN.sendingDomain}`;
          }
          replyToEmail = options.fromEmail;
          console.log('📧 Using manually generated subdomain address:', fromEmail);
        }
      } else if (options.fromEmail && !options.fromEmail.endsWith('@scubamail.io') && !options.fromEmail.endsWith('@scaledots.com')) {
        // If no userId but we have fromEmail (not official domain), generate subdomain format directly
        console.log('📧 Generating subdomain format for:', options.fromEmail);
        const [localPart, domain] = options.fromEmail.split('@');
        const cleanLocalPart = localPart.replace(/[^a-zA-Z0-9]/g, '-');
        const freeEmailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
        if (freeEmailDomains.includes(domain.toLowerCase())) {
          fromEmail = `${cleanLocalPart}.${domain}@${ACTIVE_DOMAIN.sendingDomain}`;
        } else {
          fromEmail = `${cleanLocalPart}@${ACTIVE_DOMAIN.sendingDomain}`;
        }
        replyToEmail = options.fromEmail;
        console.log('📧 Using generated subdomain address:', fromEmail);
      }

      const fromName = options.fromName || process.env.REACT_APP_FROM_NAME || 'Scubamail';

      // Do not treat tagged billing/plan emails as verification (subject heuristics can false-positive).
      const tagType = options.tags?.Type;
      const skipVerificationSubjectHeuristic =
        tagType === 'plan_activation' ||
        tagType === 'invoice' ||
        tagType === 'receipt' ||
        tagType === 'billing_notice';

      // Determine if this is a verification or system email (subject-only legacy signal)
      const isVerificationEmail =
        !skipVerificationSubjectHeuristic &&
        !!options.subject &&
        (options.subject.toLowerCase().includes('verify') ||
          options.subject.toLowerCase().includes('verification') ||
          options.subject.toLowerCase().includes('password reset') ||
          options.subject.toLowerCase().includes('reset your password'));

      // Construct headers, including List-Unsubscribe for non-system emails
      const headers: { [key: string]: string } = { ...options.headers };
      
      if (shouldAddUnsubscribeHeaders(options.subject, options.tags)) {
        const unsubHeaders = generateUnsubscribeHeaders(
          options.to, 
          options.userId, 
          options.workspaceId, 
          options.unsubscribeUrl
        );
        Object.assign(headers, unsubHeaders);
        console.log('📧 Added List-Unsubscribe headers:', headers['List-Unsubscribe']);
      }

      const sesOptions = {
        from: {
          email: fromEmail,
          name: fromName
        },
        to: [{
          email: options.to,
          name: options.to.split('@')[0] // Use email prefix as name
        }],
        subject: options.subject,
        htmlBody: options.htmlBody,
        textBody: options.textBody,
        replyTo: replyToEmail,
        tags: isVerificationEmail ? {
          Type: options.subject.toLowerCase().includes('password') ? 'password_reset' : 'verification',
          Source: 'system'
        } : options.tags,
        headers: headers
      };

      const effectiveTagType = sesOptions.tags?.Type ?? '';
      if (effectiveTagType === 'verification' || effectiveTagType === 'EmailVerification') {
        sesOptions.from.email = PASSCODE_VERIFICATION_FROM_EMAIL;
        sesOptions.replyTo = PASSCODE_VERIFICATION_FROM_EMAIL;
      }

      // Use secure email service which routes to Amazon SES via Firebase Functions
      const result = await secureEmailService.sendEmail(sesOptions);

      if (result.success) {
        const via = result.wasFallback ? `${result.provider || 'unknown'} (fallback)` : result.provider || 'unknown';
        console.log('✅ Email sent successfully:', result.messageId, 'via', via);
        console.log('📧 From:', sesOptions.from.email);
        if (sesOptions.replyTo) {
          console.log('📧 Reply-To:', sesOptions.replyTo);
        }

        // Log system email to Firestore (only when authenticated; otherwise Firestore rules reject)
        if (auth.currentUser) {
          this.logSystemEmail({
            to: options.to,
            subject: options.subject,
            from: sesOptions.from.email,
            type: sesOptions.tags?.Type || 'system',
            status: 'sent',
            messageId: result.messageId
          });
        }

        return {
          success: true,
          messageId: result.messageId,
          provider: result.provider,
          wasFallback: result.wasFallback,
        };
      } else {
        console.error('❌ SES email failed:', result.error);
        return {
          success: false,
          error: result.error
        };
      }
    } catch (error) {
      console.error('❌ SES Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email via SES'
      };
    }
  }

  // Send email with subdomain-based sending address
  static async sendEmailWithSubdomain(
    options: EmailOptions & {
      userId: string;
      fromEmail: string;
      replyToEmail?: string;
    }
  ): Promise<EmailResult> {
    try {
      console.log('📧 Sending email with subdomain-based address...');
      console.log('From Email:', options.fromEmail);
      console.log('User ID:', options.userId);

      // Get the subdomain-based sending address
      const sendingAddress = await sendingDomainService.getSendingAddressForEmail(
        options.userId,
        options.fromEmail
      );

      console.log('📧 Generated sending address:', sendingAddress);

      // Get reply-to email
      const replyTo = await sendingDomainService.getReplyToEmail(sendingAddress);
      const replyToEmail = options.replyToEmail || replyTo || options.fromEmail;

      // Send email with subdomain address
      return await this.sendEmail({
        ...options,
        fromEmail: sendingAddress,
        replyToEmail
      });
    } catch (error) {
      console.error('Error sending email with subdomain:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email with subdomain'
      };
    }
  }

  // Send verification email with template
  static async sendVerificationEmail(
    userEmail: string,
    userName: string,
    verificationCode: string
  ): Promise<EmailResult> {
    try {
      // 1. Try to get Firestore override (soft failure allowed for unauthenticated)
      let automation = null;
      try {
        automation = await platformEmailService.getSystemAutomation('user_verification');
      } catch (err) {
        console.warn('📧 ⚠️ Permission denied or error fetching Verification automation. Falling back to default.');
      }
      if (automation && automation.steps && automation.steps.length > 0) {
        const actionStep = automation.steps.find((s: any) => s.type === 'action');
        if (actionStep) {
          const config = actionStep.config as AutomationAction;
          if (config.config?.htmlBody) {
            console.log('📧 ✅ Using Firestore override for Verification Email');
            return await this.sendEmail({
              to: userEmail,
              subject: this.processSystemTemplate(config.config.subject || 'Verify Your Email', { first_name: userName, passcode: verificationCode }),
              htmlBody: this.processSystemTemplate(config.config.htmlBody, { first_name: userName, passcode: verificationCode }),
              fromName: config.config.fromName || 'Scubamail',
              fromEmail: PASSCODE_VERIFICATION_FROM_EMAIL,
              tags: { Type: 'verification', Source: 'system' }
            });
          }
        }
      }

      // 2. Fallback to legacy
      console.log('📧 Using default verification template (no override found)');

      return await this.sendDefaultVerificationEmail(userEmail, userName, verificationCode);
    } catch (error) {
      console.error('Error sending verification email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send verification email'
      };
    }
  }

  // Send password reset email with template
  static async sendPasswordResetEmail(
    userEmail: string,
    userName: string,
    resetCode: string
  ): Promise<EmailResult> {
    try {
      // 1. Try to get Firestore override (soft failure allowed for unauthenticated)
      let automation = null;
      try {
        automation = await platformEmailService.getSystemAutomation('password_reset');
      } catch (err) {
        console.warn('📧 ⚠️ Permission denied or error fetching Password Reset automation. Falling back to default.');
      }
      if (automation && automation.steps && automation.steps.length > 0) {
        const actionStep = automation.steps.find((s: any) => s.type === 'action');
        if (actionStep) {
          const config = actionStep.config as AutomationAction;
          if (config.config?.htmlBody) {
            console.log('📧 ✅ Using Firestore override for Password Reset Email');
            return await this.sendEmail({
              to: userEmail,
              subject: this.processSystemTemplate(config.config.subject || 'Reset Your Password', { first_name: userName, passcode: resetCode }),
              htmlBody: this.processSystemTemplate(config.config.htmlBody, { first_name: userName, passcode: resetCode }),
              fromName: config.config.fromName || 'Scubamail',
              fromEmail: config.config.fromEmail || 'verify@scubamail.io',
              tags: { Type: 'password_reset', Source: 'system' }
            });
          }
        }
      }

      console.log('📧 Using default password reset template (no override found)');
      return await this.sendDefaultPasswordResetEmail(userEmail, userName, resetCode);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send password reset email'
      };
    }
  }

  // Helper to replace variables in system strings
  private static processSystemTemplate(template: string, variables: Record<string, string>): string {
    if (!template) return '';
    let processed = template;
    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processed = processed.replace(regex, value || '');
    }
    return processed;
  }

  // Default verification email (fallback)
  private static async sendDefaultVerificationEmail(
    userEmail: string,
    userName: string,
    verificationCode: string
  ): Promise<EmailResult> {
    const htmlBody = SystemEmailService.getPasscodeEmail(userName, verificationCode, 'email_verification');
    const textBody = `Verify Your Email - Scubamail\n\nHi ${userName},\n\nYour verification code is: ${verificationCode}\n\nThis code will expire in 10 minutes.`;

    return await this.sendEmail({
      to: userEmail,
      subject: 'Verify Your Email - Scubamail',
      htmlBody,
      textBody,
      fromName: 'Scubamail',
      fromEmail: PASSCODE_VERIFICATION_FROM_EMAIL,
      tags: { Type: 'verification', Source: 'system' }
    });
  }

  // Plan activation / update email
  static async sendPlanActivationEmail(params: {
    to: string;
    userName: string;
    planName: string;
    amount: number;
    currency: string;
    billingPeriod: 'monthly' | 'annual';
    startDate: Date;
    endDate: Date;
    invoiceDownloadUrl?: string; // Direct download URL for invoice PDF
  }): Promise<EmailResult> {
    const { to, planName } = params;

    const fmt = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const templateVars: Record<string, string> = {
      first_name: params.userName || 'there',
      plan_name: planName,
      billing_period: params.billingPeriod === 'annual' ? 'Annual' : 'Monthly',
      amount: String(params.amount),
      currency: (params.currency || 'USD').toUpperCase(),
      start_date: fmt(params.startDate),
      end_date: fmt(params.endDate),
    };

    let subject = `Your ${planName} plan is now active`;
    let htmlBody = SystemEmailService.getPlanActivationEmail(params);
    let fromEmail = BILLING_FROM_EMAIL;
    let fromName = 'Scubamail';

    // Check for Firestore override
    try {
      const automation = await platformEmailService.getSystemAutomation('plan_activation');
      if (automation && automation.steps && automation.steps.length > 0) {
        const actionStep = automation.steps.find((s: any) => s.type === 'action');
        const cfg = actionStep?.config as any;
        const innerCfg = cfg?.config ?? cfg;
        if (innerCfg?.htmlBody) {
          console.log('📧 ✅ Using Firestore override for Plan Activation email');
          subject = this.processSystemTemplate(innerCfg.subject || subject, templateVars);
          htmlBody = this.processSystemTemplate(innerCfg.htmlBody, templateVars);
          fromEmail = innerCfg.fromEmail || fromEmail;
          fromName = innerCfg.fromName || fromName;
        }
      }
    } catch (overrideErr) {
      console.warn('📧 Plan activation Firestore override fetch failed, using default:', overrideErr);
    }

    const textBody = `Your ${planName} plan is now active\n\nHi ${params.userName || 'there'},\n\nYour subscription has been updated successfully.\n\nPlan: ${planName}\nBilling: ${params.billingPeriod}\nAmount: ${params.amount} ${params.currency}\n\nManage your billing: ${mainAppUrl('/settings/billing')}`;

    return await this.sendEmail({
      to,
      subject,
      htmlBody,
      textBody,
      fromName,
      fromEmail,
      replyToEmail: BILLING_FROM_EMAIL,
      tags: {
        Source: 'system',
        Type: 'plan_activation'
      }
    });
  }

  // Default password reset email (fallback)
  private static async sendDefaultPasswordResetEmail(
    userEmail: string,
    userName: string,
    resetCode: string
  ): Promise<EmailResult> {
    const htmlBody = SystemEmailService.getPasscodeEmail(userName, resetCode, 'password_reset');
    const textBody = `Reset Your Password - Scubamail\n\nHi ${userName},\n\nYour password reset code is: ${resetCode}\n\nThis code will expire in 10 minutes.`;

    return await this.sendEmail({
      to: userEmail,
      subject: 'Reset Your Password - Scubamail',
      htmlBody,
      textBody,
      fromName: 'Scubamail'
    });
  }

  // Log system emails to Firestore
  private static async logSystemEmail(params: {
    to: string;
    subject: string;
    from: string;
    type: string;
    status: 'sent' | 'failed';
    messageId?: string;
  }): Promise<void> {
    try {
      await addDoc(collection(db, 'email_logs'), {
        ...params,
        timestamp: serverTimestamp(),
        createdAt: new Date()
      });
      console.log('📝 System email logged to Firestore');
    } catch (error) {
      console.error('Failed to log system email:', error);
      // Don't fail the email sending if logging fails
    }
  }
}

export default EmailService;
